import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KoobButton } from '@koob/ui';
import { getCancellableFolderBookings } from '@koob/utils';
import FolderCancelAllBookingsModal, {
  FolderCancellableBooking,
} from './FolderCancelAllBookingsModal';

export interface FolderCancelAllBookingsButtonProps {
  /** The folder bookings; only the cancellable ones are offered. */
  bookings: FolderCancellableBooking[];
  /** Sends the cancellation; resolves true once done, which closes the modal. */
  onSubmit: (values: {
    bookingIds: FolderCancellableBooking['id'][];
    reason: string;
  }) => Promise<boolean>;
}

/**
 * KD-3842 "Cancel all bookings" of a client folder: hidden without any
 * cancellable booking. Each app sends the cancellation through its own API.
 */
export default function FolderCancelAllBookingsButton({
  bookings,
  onSubmit,
}: FolderCancelAllBookingsButtonProps) {
  const { t } = useTranslation('folders');
  const [open, setOpen] = useState(false);
  const cancellableBookings = getCancellableFolderBookings(bookings);

  if (cancellableBookings.length === 0) {
    return null;
  }

  return (
    <>
      <KoobButton
        variant="dangerOutline"
        onClick={() => setOpen(true)}
        data-testid="cancel-all-bookings"
      >
        <i className="fa-regular fa-ban" aria-hidden="true" />
        {t('cancelAll.button')}
      </KoobButton>

      <FolderCancelAllBookingsModal
        open={open}
        setOpen={setOpen}
        bookings={cancellableBookings}
        onSubmit={async (values) => {
          if (await onSubmit(values)) {
            setOpen(false);
          }
        }}
      />
    </>
  );
}
